import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Plus, X } from 'lucide-react';

interface AddGroupRowProps {
  sectionId: string;
  subsectionId: string;
  onAddGroup: (sectionId: string, subsectionId: string, group: PolicyFormQuestionGroup) => void;
}

export function AddGroupRow({ sectionId, subsectionId, onAddGroup }: AddGroupRowProps) {
  const [showAddRow, setShowAddRow] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [format, setFormat] = useState<'default' | 'grid' | 'inline'>('default');
  const [align, setAlign] = useState<'start' | 'center' | 'end'>('start');

  const handleSave = () => {
    const newGroup: PolicyFormQuestionGroup = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2),
      title,
      description,
      format,
      align,
      questions: [],
    };

    onAddGroup(sectionId, subsectionId, newGroup);
    setShowAddRow(false);
    setTitle('');
    setDescription('');
    setFormat('default');
    setAlign('start');
  };

  const onToggleShow = () => {
    setShowAddRow(prev => !prev);
  };

  if (!showAddRow) {
    return (
      <Button
        size="sm"
        variant="outline"
        onClick={onToggleShow}
      >
        <Plus
          size={16}
          className="mr-1"
        />{' '}
        Add Group
      </Button>
    );
  }

  return (
    <Card className="space-y-4 rounded-lg border bg-gray-50 p-4 shadow-sm">
      <div className="grid grid-cols-3 gap-4">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Group Title"
          className="col-span-1 border-b border-transparent bg-transparent text-sm outline-none focus:border-gray-400"
        />
        <input
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Group Description"
          className="col-span-2 border-b border-transparent bg-transparent text-sm outline-none focus:border-gray-400"
        />
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-700">Format</label>
          <Select
            value={format}
            onValueChange={v => setFormat(v as any)}
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="grid">Grid</SelectItem>
              <SelectItem value="inline">Inline</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-700">Align</label>
          <Select
            value={align}
            onValueChange={v => setAlign(v as any)}
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder="Align" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="start">Start</SelectItem>
              <SelectItem value="center">Center</SelectItem>
              <SelectItem value="end">End</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          onClick={handleSave}
        >
          <Plus
            size={12}
            className="mr-1"
          />{' '}
          Save Group
        </Button>
        <Button
          size="sm"
          variant="destructive"
          onClick={onToggleShow}
        >
          <X
            size={12}
            className="mr-1"
          />{' '}
          Cancel
        </Button>
      </div>
    </Card>
  );
}
